import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { bookingAPI, reviewAPI } from '../../api';
import { Badge, Spinner, EmptyState, Pagination, Modal } from '../../components/common/Misc';
import Button from '../../components/common/Button';
import styles from './BookingsPage.module.css';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
const resolveImg = (url) => (url && url.startsWith('http') ? url : `${API_BASE}${url}`);


const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [filter,   setFilter]   = useState('');
  const [reviewModal, setReviewModal] = useState(null); // booking object

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 8 };
      if (filter) params.status = filter;
      const res = await bookingAPI.getMy(params);
      setBookings(res.data.data);
      setPages(res.data.pages);
    } catch { setBookings([]); }
    finally  { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await bookingAPI.updateStatus(id, { status: 'cancelled' });
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to cancel'); }
  };

  const STATUSES = ['', 'pending', 'confirmed', 'active', 'completed', 'cancelled'];

  return (
    <div className="container">
      <div className={styles.header}>
        <h1 className={styles.title}>My bookings</h1>
        <div className={styles.filters}>
          {STATUSES.map(s => (
            <button
              key={s}
              className={`${styles.filterBtn} ${filter === s ? styles.active : ''}`}
              onClick={() => { setFilter(s); setPage(1); }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : bookings.length === 0
        ? <EmptyState icon="📋" title="No bookings found" subtitle="Your bookings will appear here" />
        : (
          <div className={styles.list}>
            {bookings.map(b => (
              <BookingCard
                key={b._id}
                booking={b}
                onCancel={() => handleCancel(b._id)}
                onReview={() => setReviewModal(b)}
              />
            ))}
          </div>
        )
      }

      <Pagination page={page} pages={pages} onChange={setPage} />

      {reviewModal && (
        <ReviewModal
          booking={reviewModal}
          onClose={() => setReviewModal(null)}
          onSuccess={() => { setReviewModal(null); fetchBookings(); }}
        />
      )}
    </div>
  );
};

const BookingCard = ({ booking, onCancel, onReview }) => {
  const bike      = booking.bike_id;
  const canCancel = ['pending', 'confirmed'].includes(booking.status);
  const canReview = booking.status === 'completed';

  return (
    <div className={styles.card}>
      <div className={styles.bikeImg}>
        {bike?.images?.[0] ? <img src={resolveImg(bike.images[0])} alt={bike.name} /> : <span>🚲</span>}
      </div>
      <div className={styles.info}>
        <div className={styles.row}>
          <h3 className={styles.bikeName}>{bike?.name || 'Bike'}</h3>
          <Badge label={booking.status} status={booking.status} />
        </div>
        <p className={styles.bikeMeta}>{bike?.brand} · {bike?.type}</p>
        <div className={styles.dates}>
          <span>🗓 {format(new Date(booking.start_time), 'dd MMM yyyy, HH:mm')}</span>
          <span className={styles.arrow}>→</span>
          <span>{format(new Date(booking.end_time), 'dd MMM yyyy, HH:mm')}</span>
        </div>
        {booking.promo_id && (
          <p className={styles.promo}>🏷 Promo: {booking.promo_id.code}</p>
        )}
      </div>
      <div className={styles.right}>
        <div className={styles.amount}>
          {booking.discount_amount > 0 && (
            <span className={styles.original}>₹{booking.total_amount.toFixed(2)}</span>
          )}
          <span className={styles.final}>₹{booking.final_amount.toFixed(2)}</span>
        <span style={{fontSize:".7rem",color:"var(--color-text-muted)"}}>incl. 18% GST</span>
        </div>
        <div className={styles.actions}>
          {canCancel && <Button variant="danger"  size="sm" onClick={onCancel}>Cancel</Button>}
          {canReview && <Button variant="success" size="sm" onClick={onReview}>Review</Button>}
        </div>
      </div>
    </div>
  );
};

const ReviewModal = ({ booking, onClose, onSuccess }) => {
  const [rating,  setRating]  = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await reviewAPI.create({ booking_id: booking._id, rating, comment });
      toast.success('Review submitted!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally { setLoading(false); }
  };

  return (
    <Modal title={`Review  ${booking.bike_id?.name}`} onClose={onClose}>
      <div className={styles.reviewForm}>
        <div className={styles.stars}>
          {[1,2,3,4,5].map(n => (
            <button key={n} className={`${styles.star} ${n <= rating ? styles.starActive : ''}`} onClick={() => setRating(n)}>
              ★
            </button>
          ))}
        </div>
        <textarea
          className={styles.textarea}
          placeholder="Share your experience (optional)"
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={4}
        />
        <Button fullWidth loading={loading} onClick={handleSubmit}>Submit review</Button>
      </div>
    </Modal>
  );
};

export default BookingsPage;
